// When to offer the contact form. It used to wait for the third question,
// while half the visitors asked one and left, and one of them typed a phone
// number into the chat that nobody saw (docs/016). Now the moment decides:
//   - contact data typed in the chat: the form opens with it filled in;
//   - asking for a person, a call or a visit: the form opens;
//   - buying or reserving: the form is offered (one tap to open).

export interface LeadTrigger {
  /** "open": show the form at once; "offer": show the one-line offer first. */
  show: "open" | "offer";
  reason: "contact-data" | "wants-person" | "wants-to-buy";
  phone?: string;
  email?: string;
}

const EMAIL = /[^\s@<>()"',;]+@[^\s@<>()"',;]+\.[a-z]{2,}/i;
/**
 * A Polish number: nine digits, maybe after +48, in groups of any spacing.
 * Prices are fewer digits or end in "zł"; a comma or a dot counts only before
 * another digit (a decimal), so "600 123 456." ending a sentence is a number.
 * The API spots the same numbers (apps/api/src/chat/contact.ts).
 */
const PHONE = /(?<!\d|\d[,.])(?:\+?48[\s-]?)?\d(?:[\s-]?\d){8}(?!\d|[,.]\d|\s?(?:zł|pln|tys))/i;
/** A person, a call back, a meeting or a viewing (normalize()d text). */
const WANTS_PERSON = /kontakt|oddzwo|zadzwo|telefon do|numer do|czlowiek|doradc|handlowc|konsultant|spotkan|umowi|prezentacj|obejrz|wizyt/;
const WANTS_TO_BUY = /rezerw|kupi[ce]|kupn|zaliczk|umow[ae] (?:rezerwacyjn|deweloper)/;

function normalize(text: string): string {
  return text.toLowerCase().replace(/ł/g, "l").normalize("NFD").replace(/[̀-ͯ]/g, "");
}

export function leadTrigger(text: string): LeadTrigger | null {
  const email = text.match(EMAIL)?.[0];
  const phone = text.match(PHONE)?.[0]?.replace(/[\s-]/g, "");
  if (email || phone) return { show: "open", reason: "contact-data", ...(phone ? { phone } : {}), ...(email ? { email } : {}) };
  const t = normalize(text);
  if (WANTS_PERSON.test(t)) return { show: "open", reason: "wants-person" };
  if (WANTS_TO_BUY.test(t)) return { show: "offer", reason: "wants-to-buy" };
  return null;
}
